<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: Did the household save or overspend in each period of the window?
  Answer:   the net per period, positive above the line and negative below it.
  Context:  the savings rate for the window and the period table.
  Controls: the window, bucket and tab strip in the frame.
-->
<script setup lang="ts">
import { formatRatioPercent, type Locale } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  CashFlowBarChart,
  CashFlowTable,
  canViewReporting,
  chartPeriods,
  ReportFrame,
  useCashFlowQuery,
  useReportScope,
} from '@/features/reporting'

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as Locale)
const scope = useReportScope(() => canViewReporting(scope.session.can))
const { session, state, displayCurrency } = scope
const cashFlow = useCashFlowQuery(scope.flows())
const periods = computed(() =>
  chartPeriods(cashFlow.data.value?.periods ?? [], state.range.value, displayCurrency.value),
)
const ascending = computed(() => [...periods.value].reverse())
const savingsText = computed(() => {
  const rate = cashFlow.data.value?.savingsRate
  if (rate === undefined) return '—'
  return `${formatRatioPercent(rate, { locale: currentLocale.value, minimumFractionDigits: 0, maximumFractionDigits: 0 })}%`
})
</script>

<template>
  <ReportFrame :title="t('reporting.title')" :subtitle="t('reporting.overview.subtitle')" :state="state">
    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>
    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('reporting.noLedger') }}
    </p>
    <p v-else-if="!canViewReporting(session.can)" class="text-[color:var(--color-text-muted)]">
      {{ t('reporting.noAccess') }}
    </p>
    <template v-else-if="cashFlow.data.value">
      <p class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
        {{ t('reporting.trends.savingsRate', { rate: savingsText }) }}
      </p>
      <figure
        class="flex min-w-0 flex-col gap-[var(--space-group)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-[var(--space-group)]"
      >
        <figcaption class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
          {{ t('reporting.trends.netTitle') }}
        </figcaption>
        <CashFlowBarChart
          :periods="periods"
          :currency="displayCurrency"
          :bucket="state.range.value.bucket"
          mode="net"
        />
      </figure>
      <CashFlowTable
        :periods="ascending"
        :totals="cashFlow.data.value"
        :caption="t('reporting.tabs.trends')"
      />
    </template>
  </ReportFrame>
</template>
